import { Injectable, Output, EventEmitter } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserConfigService } from './user-config.service';
import { BecareService } from './becare.service';
import { CNPatient } from '../models/cnpatient';
import { ODBService } from './odb.service';
import { ReportService } from './report.service';
import * as moment from 'moment';

export class PatientSearchResult {
    constructor(public id: number,
                public lastname: string,
                public firstname: string,
                public birthday: string,
                public sex: string,
                public niss?: string,
                public address?: string,
                public zip?: string,
                public city?: string,
                public mutregime?: string,
                public mutnumber?: string,
                public type?: string) {
    }
  }
export interface PatientSelectedModule {
    name: string;
    function: string;
}
export interface PatientSelectedEvent {
    success: boolean;
    access: boolean;
    mandate: boolean;
    module?: PatientSelectedModule;
}
export interface PatientPopupEvent {
    info: any;
}

declare let gPatientId: any;

@Injectable({
  providedIn: 'root'
})
export class PatientService {

  private static instance: PatientService = null;

  info = '';
  patientid = -1;
  patient: CNPatient;
  selectedpatientresult: PatientSearchResult;
  searchingpatient = false;
  config: any = {};
  patientMenus: any;
  patientInfos: any;
  patientIcons: any;

  @Output() patientLoadedEvent = new EventEmitter<PatientSelectedEvent>();
  @Output() patientPopupEvent = new EventEmitter<PatientPopupEvent>();
  @Output() patientSelectedEvent = new EventEmitter<CNPatient>();

  constructor(private http: HttpClient, public userConfig: UserConfigService,
              private becare: BecareService, private odb: ODBService) {
    // Check if the selected patient is stored locally
    PatientService.instance = this;
    this.userConfig.configLoaded.subscribe(config => {
      this.becare.call('/user/' + config.user.id  + '/patient').subscribe(result => {
        if (result.success === true) {
          this.patientid = result.patientid;
          this.loadPatient(this.patientid)
          .subscribe(patient => {
              this.select(patient).subscribe(access => {
                this.odb.itemById(this.patientid).subscribe(item => {
                  this.patient = item as CNPatient;
                  this.patientLoadedEvent.emit(access);
                });
              });
          });
        }
      });
    });
    this.becare.authStatus().subscribe(status => {
      if (status === false) {
        // Logoff ... Clean patient
        this.patientid = -1;
        this.patient = undefined;
        this.config = {};
        this.patientMenus = undefined;
        this.patientInfos = undefined;
        this.patientIcons = undefined;
      }
    });
  }

  public static getInstance(): PatientService {
    return PatientService.instance;
  }

  loadPatient(patientid): Observable<PatientSearchResult> {
    return this.becare.call('/user/' + this.userConfig.userid  + '/patient/search', [], {
      request: {
        id: patientid
      }
    }, 'post')
    .pipe(map(result => {
      // Convert return object to PatientSearchResult
      const patient = (result as any).patients[0];
      return new PatientSearchResult(
        patient.id,
        patient.lastname,
        patient.firstname,
        patient.birthday,
        patient.sex,
        patient.niss,
        patient.address,
        patient.zip,
        patient.city,
        patient.mutregime,
        patient.mutnumber,
        patient.type);
    }));
  }

  updatePatient(): void {
    this.odb.itemById(this.patientid).subscribe(item => {
      this.patient = item as CNPatient;
      this.info = '<div class="patientinfocontainer"><div class="patientname"><span class="PatientInfo"><u>' +
        this.patient.lastname + '</u> ' +
        this.patient.firstname + '</span></div><div class="patientbirthdate"><span class="PatientInfo">' +
        moment(this.patient.birthday).format('DD/MM/YYYY') + ' ' +
        this.patient.sex + '</span></div></div>';
    });
  }

  select(patient: PatientSearchResult | number) {
    let patientid: number;
    if (patient instanceof PatientSearchResult) {
      patientid = patient.id;
    } else {
      patientid = patient;
    }
      // First call select
    return this.selectPatient(patientid)
      .pipe(map(result => {
          if ((result as any).success === false) {
              return result;
          }
          if ((result as any).access === false) {
              // No access for this patient
              return result;
          }
          this.patientid = patientid;
          gPatientId = patientid;
          this.odb.itemById(this.patientid).subscribe(item => {
            this.patient = item as CNPatient;
            this.info = '<div class="patientinfocontainer"><div class="patientname"><span class="PatientInfo"><u>' +
              this.patient.lastname + '</u> ' +
              this.patient.firstname + '</span></div><div class="patientbirthdate"><span class="PatientInfo">' +
              moment(this.patient.birthday).format('DD/MM/YYYY') + ' ' +
              this.patient.sex + '</span></div></div>';
            this.loadConfig();
            this.patientSelectedEvent.emit(this.patient);
          });
        //  this.bridgeWithOldVersion();
          return result;
      }));
  }

  selectPatient(patientid): any {
      return this.becare.call('/user/' + this.userConfig.userid  + '/patient/' + patientid + '/select');
  }

  search(searchstring): Observable<PatientSearchResult[]> {
    return new Observable(observer => {
      this.searchingpatient = true;
      this.becare.call('/user/' + this.userConfig.userid  + '/patient/search', [],
        {
          request: {
              query: searchstring,
              referring: (this.userConfig.userSearchOptions.searchreferring) ? 'yes' : 'no'
          }
        },
        'post').subscribe(result => {
          const patients = [];
          if ((result as any).success === true) {
            for (const patient of (result as any).patients) {
              patients.push(new PatientSearchResult(
                patient.id,
                patient.lastname,
                patient.firstname,
                patient.birthday,
                patient.sex,
                patient.niss,
                patient.address,
                patient.zip,
                patient.city,
                patient.mutregime,
                patient.mutnumber,
                patient.type
              ));
            }
          }
          this.searchingpatient = false;
          observer.next(patients);
        }, error => {
          this.searchingpatient = false;
          observer.error(error);
        });
    });
  }

  // patient config
  loadConfig() {
      this.becare.call('/user/' + this.userConfig.userid  + '/patient/' + this.patientid + '/ui/config/pwa')
      .subscribe(result => {
          this.config = result.config;
          // Sort user menus by index
          const usrMenus = [];
          for (const menu of this.config.menus.patient) {
              if (typeof menu === 'object') {
                  usrMenus.push(menu);
              }
          }
          this.patientMenus = usrMenus.sort((obj1, obj2) => {
              return obj1.index - obj2.index;
          });
          const infoMenus = [];
          for (const info of this.config.infos.patient) {
              if (typeof info === 'object') {
                  switch (info.type) {
                    case 'display':
                        infoMenus.push(info);
                        break;
                    case 'popup':
                        // Event with info to popup
                        this.patientPopupEvent.emit(info);
                        break;
                  }
              }
          }
          this.patientInfos = infoMenus.sort((obj1, obj2) => {
              return obj1.index - obj2.index;
          });
          // Sort icon menus by index
          const iconMenus = [];
          for (const menu of this.config.icons.patient) {
              if (typeof menu === 'object') {
                iconMenus.push(menu);
              }
          }
          this.patientIcons = iconMenus.sort((obj1, obj2) => {
              return obj1.index - obj2.index;
          });

      });
  }

  // Update Old HTML and JS variables and Objects
  bridgeWithOldVersion() {
      // Set global gPatientID
//      gPatientId = this.patientid;
      // Load patient DMG
  }
}
